import { BILLING_MODELS } from '../../constants';
import { SCHEDULE_CHAIN, etapasContratadas } from '../../constants/schedule';

/**
 * Entrada por embrião transferido.
 *
 * Um valor por embrião que sai no lançamento da TE e é cobrado como entrada.
 * R$95 é o padrão de reserva, não uma regra: a taxa vem do laboratório e pode
 * ser ajustada no serviço.
 *
 * A regra que dá trabalho é o encontro de contas no fim: a entrada abate o
 * faturamento final SÓ NA MEDIDA EM QUE FOI PAGA. Emitida e não paga não abate
 * nada — senão o cliente ganharia desconto por uma cobrança que ele ignorou.
 */

/**
 * Taxa a usar: a congelada no serviço manda; senão a do laboratório.
 *
 * SEM RESERVA CRAVADA. Havia um `ENTRADA_PADRAO = 95` no código que entrava
 * quando nada estava configurado — um preço inventado, no meio de um arquivo,
 * cobrando de cliente real. Laboratório sem taxa configurada agora devolve
 * zero, e zero faz `entradaAEmitir` recusar com "taxa zerada" em vez de
 * cobrar um valor que ninguém combinou.
 *
 * A taxa é de três níveis, do mais específico para o mais geral: o serviço
 * congela a dele no lançamento da TE, o laboratório tem a sua no contrato, e
 * não havendo nenhuma das duas não há entrada.
 */
export const taxaDeEntrada = (service, laboratory) => {
  const doServico = parseFloat(service?.entrada_por_embriao);
  if (Number.isFinite(doServico)) return doServico;

  const doLab = parseFloat(laboratory?.entrada_por_embriao);
  return Number.isFinite(doLab) ? doLab : 0;
};

/**
 * A taxa que o formulário de serviço NOVO já traz no campo.
 *
 * Mesma regra dos preços padrão: só preenche, e zero não preenche (zero no
 * laboratório é "não configurado"). Ao salvar, a taxa fica no serviço — mudar
 * a do laboratório depois não muda a deste contrato.
 */
export const entradaParaServicoNovo = (laboratory) => {
  const doLab = parseFloat(laboratory?.entrada_por_embriao);
  return Number.isFinite(doLab) && doLab > 0 ? { entrada_por_embriao: doLab } : {};
};

/**
 * Quanto vale a entrada deste serviço.
 *
 * A quantidade preferida é a congelada no lançamento da TE. `te_quantity` é a
 * reserva para serviço lançado antes desta funcionalidade existir — sem ela, a
 * entrada de todo serviço antigo apareceria zerada.
 */
export const calcularEntrada = (service, laboratory) => {
  const quantidade = Number.isFinite(Number(service?.entrada_embrioes))
    && service?.entrada_embrioes !== null
      ? Number(service.entrada_embrioes)
      : Number(service?.te_quantity) || 0;

  const taxa = taxaDeEntrada(service, laboratory);
  return {
    quantidade,
    taxa,
    valor: Math.round(quantidade * taxa * 100) / 100,
  };
};

/**
 * Vale oferecer a emissão da faturinha da entrada agora?
 *
 * Chamada no momento em que a TE é salva, que é quando a pessoa ainda está
 * com os números na cabeça. Antes disto a entrada só podia ser emitida no
 * Financeiro, numa segunda visita à tela — e em produção ela nunca foi
 * emitida uma única vez, com cinco serviços elegíveis parados.
 *
 * Os quatro "nãos", cada um por um motivo diferente:
 *
 *   - TE não concluída ainda vai mudar de número. O workflow inteiro salva
 *     junto, então sem esta trava o convite reapareceria a cada gravação da
 *     OPU ou da FIV de um serviço que já tem transferidos — e a pergunta que
 *     volta sozinha é a que se aprende a fechar sem ler;
 *   - sem embrião transferido não há o que cobrar;
 *   - taxa ZERO é uma decisão, não um esquecimento. Quem zera o campo na TE
 *     está dizendo que este serviço não tem entrada, e perguntar de novo a
 *     cada salvamento seria ignorar a resposta;
 *   - já emitida não se emite de novo. Repare que o critério é EMITIDA, não
 *     paga: a fatura existe assim que sai, e esperar o pagamento para parar
 *     de oferecer produz duas cobranças do mesmo embrião;
 *   - serviço já faturado fechou a conta. Entrada depois do acerto final não
 *     tem o que adiantar.
 *
 * Nenhum destes fecha a porta: o botão "Cobrar entrada" do Financeiro segue
 * valendo para o caso fora do padrão. Isto aqui decide só quando o sistema
 * toma a iniciativa de perguntar.
 */
/** Por que este modelo não comporta entrada. */
const motivoDoModelo = (service) => {
  if (service?.billing_model === BILLING_MODELS.SEM_COBRANCA) return 'servico_sem_cobranca';
  if (service?.billing_model === BILLING_MODELS.DIARIA) return 'diaria_nao_adianta';
  return 'faturamento_fecha_antes';
};

export const entradaAEmitir = (service, laboratory, jaEmitida = false) => {
  const { quantidade, taxa, valor } = calcularEntrada(service, laboratory);

  const motivo =
    !service?.te_done ? 'te_em_andamento'
    : quantidade <= 0 ? 'sem_transferidos'
    : taxa <= 0       ? 'sem_taxa'
    : jaEmitida       ? 'ja_emitida'
    : service?.billed ? 'servico_faturado'
    // ADIANTAMENTO SÓ EXISTE SE HOUVER O QUE ADIANTAR.
    //
    // A entrada é paga ANTES de o serviço fechar. Se o faturamento final
    // acontece na própria TE, ou antes dela, não há distância a cobrir: a
    // cobrança de verdade já está disponível, e a entrada seria um segundo
    // documento pelos mesmos embriões. A regra mora em `entradaCabeNoModelo`
    // e é derivada da corrente, não de uma lista de modelos.
    //
    // Foi a falta disto que deixou dois serviços por TE com entrada de R$ 95
    // por embrião sobre tarifa contratada de R$ 85 e R$ 80.
    : !entradaCabeNoModelo(service) ? motivoDoModelo(service)
    : null;

  return { deve: motivo === null, motivo, quantidade, taxa, valor };
};

/**
 * A entrada faz sentido NESTE modelo de cobrança?
 *
 * A ENTRADA É UM ADIANTAMENTO, e adiantamento só existe quando há distância
 * entre o trabalho e o acerto. Ela é cobrada no lançamento da TE; se o
 * faturamento final do serviço acontece NA TE ou ANTES dela, não há o que
 * adiantar — a cobrança de verdade já está disponível, e emitir a entrada só
 * criaria um segundo documento pelos mesmos embriões.
 *
 * Por isso a regra é DERIVADA, e não uma lista de modelos: cabe entrada
 * quando a etapa que fecha o faturamento vem depois da TE na corrente.
 *
 *   por prenhez   fecha na DG60, uns cinquenta dias depois da TE   cabe
 *   pacote        fecha na última etapa contratada; com conferência
 *                 contratada, cabe; sem ela, fecha na própria TE     depende
 *   por TE        fecha NA TE                                       não cabe
 *   por embrião   fecha na FIV, que acontece ANTES da TE            não cabe
 *   diária        corre por dia trabalhado, não tem etapa           não cabe
 *   sem cobrança  não fatura nada aqui                              não cabe
 *
 * Derivar em vez de listar é o que mantém a regra certa quando o contrato
 * muda: um pacote que passa a incluir conferência ganha a entrada sozinho.
 */
const ETAPA_QUE_FECHA = {
  [BILLING_MODELS.POR_TE]: () => 'te',
  [BILLING_MODELS.POR_PRENHEZ]: () => 'dg60',
  [BILLING_MODELS.POR_EMBRIAO]: () => 'fiv',
  [BILLING_MODELS.PACOTE_COMPLETO]: (s) => ultimaContratada(s),
  [BILLING_MODELS.PERSONALIZADO]: (s) => ultimaContratada(s),
};

const ordem = (id) => SCHEDULE_CHAIN.findIndex(e => e.id === id);

const ultimaContratada = (service) => {
  const etapas = etapasContratadas(service);
  return etapas.length > 0 ? etapas[etapas.length - 1].id : null;
};

/** A etapa em que o faturamento deste serviço fecha, ou nada. */
export const etapaQueFecha = (service) =>
  ETAPA_QUE_FECHA[service?.billing_model]?.(service) || null;

export const entradaCabeNoModelo = (service) => {
  const fecha = etapaQueFecha(service);
  if (!fecha) return false;
  return ordem(fecha) > ordem('te');
};

/**
 * O QUE o prazo de 21 dias da TE cobra neste serviço.
 *
 * Os 21 dias são do laboratório, não da entrada: eles contam da TRANSFERÊNCIA,
 * porque é ali que o cliente recebe o que comprou. O que muda de modelo para
 * modelo é o nome do documento que vence nessa data.
 *
 *   'entrada'    o faturamento fecha DEPOIS da TE, então existe adiantamento
 *                a cobrar até lá — por prenhez, pacote com conferência;
 *
 *   'pagamento'  o faturamento fecha NA TE. Não há entrada, porque não há
 *                distância a adiantar, MAS os 21 dias continuam valendo: é o
 *                prazo da fatura do próprio serviço. É o caso do "por
 *                transferência", que é a maioria em produção;
 *
 *   null         não há o que cobrar aqui. Só o `sem_cobranca`, cujo
 *                faturamento acontece em outro serviço.
 *
 * O QUE MANDA É A TRANSFERÊNCIA TER ACONTECIDO, e não o modelo de cobrança.
 * A primeira versão disto amarrava o prazo à etapa que fecha o faturamento, e
 * por isso devolvia `null` para quem compra embrião pronto e transfere: aquele
 * serviço fecha na FIV, que nem foi feita no laboratório, e ficava sem prazo
 * nenhum — nem o derivado no calendário, nem a sugestão na emissão. O prazo do
 * laboratório é dos 21 dias da TE, para toda transferência: embrião comprado,
 * embrião produzido aqui, cobrança por embrião transferido ou entrada.
 *
 * Quem não transferiu não tem prazo, e isso `vencimentoDesdeATE` resolve
 * sozinho devolvendo nulo sem `te_done` — é por isso que esta função não
 * pergunta pela TE.
 */
export const relogioDaTE = (service) => {
  const modelo = service?.billing_model;
  // Modelo ausente ou desconhecido não ganha prazo: serviço que ainda não disse
  // como cobra não tem cobrança a vencer, e chutar um prazo aqui colocaria uma
  // data no calendário de um serviço que nem preço tem.
  if (!Object.values(BILLING_MODELS).includes(modelo)) return null;
  // O `sem_cobranca` fatura em OUTRO serviço, e é lá que o prazo corre.
  if (modelo === BILLING_MODELS.SEM_COBRANCA) return null;
  return entradaCabeNoModelo(service) ? 'entrada' : 'pagamento';
};

/**
 * O QUE ESTÁ ERRADO NUMA ENTRADA JÁ EMITIDA.
 *
 * As travas de `entradaAEmitir` impedem a emissão errada de hoje em diante, e
 * não fazem nada pelo documento que já saiu. Em produção são dois serviços por
 * TE com entrada viva — 2026-0037, R$ 2.280 sobre um serviço de R$ 2.125, e
 * 2026-0038, R$ 1.805 sobre R$ 1.520 — e a tela os mostrava como qualquer
 * outro: "Entrada R$ 1.805 em fatura 2026-0034", sem nada dizendo que aquela
 * cobrança não devia existir e que ela é maior que o serviço inteiro.
 *
 * Pior: enquanto o serviço não é faturado, é a entrada que responde por ele na
 * conta. O cliente aparecia devendo R$ 1.805 por um trabalho de R$ 1.520.
 *
 * Duas perguntas independentes, e por isso uma lista e não um booleano: um
 * modelo que não comporta entrada pode ter emitido um valor pequeno, e um
 * modelo que comporta pode ter emitido um valor absurdo. A tela precisa dizer
 * QUAL é o caso para a pessoa saber o que fazer.
 */
export const PROBLEMA_DA_ENTRADA = {
  modelo_nao_comporta: 'este modelo fecha o faturamento na própria TE, então não '
    + 'há adiantamento a cobrar — a entrada emitida cobra os mesmos embriões duas vezes',
  sem_cobranca_aqui: 'este serviço não fatura aqui: o acerto acontece em outro, '
    + 'e a entrada emitida cobra de um serviço que não tem conta',
  maior_que_o_servico: 'a entrada emitida é maior que o valor do serviço',
};

export const problemasDaEntrada = (
  service, { entradaFaturada = 0, valorDoServico = 0 } = {},
) => {
  // Sem documento emitido não há o que estar errado: o que não cabe no modelo
  // e nunca foi emitido é assunto de `entradaAEmitir`, que recusa e explica.
  if (Number(entradaFaturada) <= 0) return [];

  const problemas = [];
  if (service?.billing_model === BILLING_MODELS.SEM_COBRANCA) {
    problemas.push('sem_cobranca_aqui');
  } else if (!entradaCabeNoModelo(service)) {
    problemas.push('modelo_nao_comporta');
  }

  // `valorDoServico` zero é "a etapa que fecha ainda não aconteceu", e não
  // "o serviço não vale nada": comparar contra ele acusaria toda entrada em
  // andamento, que é exatamente para o que a entrada existe.
  if (Number(valorDoServico) > 0 && Number(entradaFaturada) > Number(valorDoServico)) {
    problemas.push('maior_que_o_servico');
  }

  return problemas;
};

/**
 * A CONTA DE UM SERVIÇO: quanto foi cobrado, quanto entrou, quanto falta.
 *
 * A regra do laboratório, numa frase: A FATURA DO SERVIÇO SUPERA A DA ENTRADA.
 *
 * A entrada é parte do serviço, cobrada antes. Dez embriões a R$ 150 são
 * R$ 1.500 de serviço, e a entrada de R$ 95 por embrião cobra R$ 950 desses
 * mesmos R$ 1.500 — não é um acréscimo. Então:
 *
 *   enquanto o serviço NÃO foi faturado, a entrada é a única cobrança que
 *   existe, e é ela que conta;
 *
 *   assim que o serviço É faturado, o que vale é a fatura dele, pelo PREÇO
 *   CHEIO. A entrada deixa de contar como cobrança à parte, senão os mesmos
 *   embriões apareceriam duas vezes;
 *
 *   o que a entrada já RECEBEU continua valendo, e é ele que abate — no
 *   SALDO, não no valor da fatura. O papel que vai ao cliente diz R$ 1.500,
 *   e o que ele ainda deve é R$ 550.
 *
 * É diferente de descontar no documento, que foi como eu tinha feito antes:
 * ali a fatura saía por R$ 550 e o preço do serviço sumia do registro.
 *
 * QUEM DITA O VALOR É O CADASTRO, e não o documento emitido.
 *
 * A versão anterior lia `billed_value`, o valor congelado no dia em que a fatura
 * saiu, com o argumento de que fatura é documento. O argumento vale para o papel
 * que foi ao cliente e não para a CONTA: quando o preço ou a quantidade mudam no
 * cadastro, é o novo número que o cliente deve, e era preciso refaturar para o
 * sistema concordar com isso. Enquanto ninguém refaturasse, a tela avisava que os
 * dois números divergiam e seguia cobrando o velho.
 *
 * Então o saldo é uma subtração só: PREÇO DO CADASTRO menos o que já entrou. O
 * que a fatura diz continua no documento, para quem precisa conferir o papel, e
 * `billed_value` fica sendo o registro histórico da emissão — não mais a conta.
 */
export const contaDoServico = ({
  billed = false, valorDoCadastro = 0,
  entradaFaturada = 0, entradaPaga = 0, servicoPago = 0,
} = {}) => {
  const c = (n) => Math.round((Number(n) || 0) * 100);

  // Faturado, a conta é o preço do cadastro. Antes de faturar, a entrada é a
  // única cobrança que existe, e é ela que conta.
  const faturado = billed ? c(valorDoCadastro) : c(entradaFaturada);

  // Caixa é caixa: o que entrou pela entrada continua contado depois de o
  // serviço ser faturado — é justamente esse dinheiro que abate o saldo.
  const recebido = c(entradaPaga) + c(servicoPago);

  return {
    faturado: faturado / 100,
    recebido: recebido / 100,
    saldo: Math.max(0, faturado - recebido) / 100,
    // Recebeu mais do que foi cobrado. Acontece quando a entrada cobrou acima
    // do serviço; o sistema não modela crédito de cliente, e por isso devolve
    // o número em vez de sumir com ele.
    credito: Math.max(0, recebido - faturado) / 100,
  };
};

/**
 * Por que a entrada não deve ser emitida agora, em português.
 *
 * Os motivos já existiam como código; faltava o texto. Cada tela escrevia a
 * sua explicação, ou não explicava nada e só escondia o botão — e botão que
 * some sem dizer por quê é a pergunta "cadê a cobrança da entrada?" chegando
 * no fim do mês.
 */
export const MOTIVO_DA_ENTRADA = {
  te_em_andamento: 'A TE ainda não foi concluída. A entrada cobra por embrião transferido, '
    + 'e esse número ainda pode mudar.',
  sem_transferidos: 'Nenhum embrião transferido neste serviço.',
  sem_taxa: 'A taxa de entrada deste serviço está zerada. Ajuste no lançamento da TE ou no '
    + 'contrato do laboratório.',
  ja_emitida: 'Este serviço já tem fatura de entrada emitida.',
  // Não vem de `entradaAEmitir`: é a tela dizendo que não conseguiu ler as
  // faturas. Antes disso existir, a falha escondia a seção inteira — sem
  // botão, sem aviso, sem nada. Da cadeira de quem usa, a função não existia.
  leitura_falhou: 'Não foi possível conferir as faturas deste serviço agora, então a entrada '
    + 'fica bloqueada para não cobrar o mesmo embrião duas vezes. Recarregue a página.',
  servico_faturado: 'Este serviço já foi faturado. O acerto final já aconteceu.',
  faturamento_fecha_antes: 'O faturamento deste serviço fecha na TE ou antes dela, então não há '
    + 'o que adiantar: a entrada cobraria os mesmos embriões um segundo tempo. Fature o '
    + 'serviço direto.',
  diaria_nao_adianta: 'Serviço por diária é cobrado por dia trabalhado, sem etapa final a '
    + 'adiantar.',
  servico_sem_cobranca: 'Este serviço está marcado como sem cobrança, e o que ele cobra é '
    + 'faturado noutro serviço. Cobre a entrada lá.',
};
